import { Helmet } from 'react-helmet-async';
import React from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  siteName?: string;
  locale?: string;
  robots?: string;
}

const SEO: React.FC<SEOProps> = ({
  title = 'PokeShani - #1 Digital Products Platform | Zero Fees | Instant Payouts | Start Free Today',
  description = 'Skyrocket your income with PokeShani! Sell digital products with ZERO platform fees, instant payouts, and no payment thresholds. Start FREE today!',
  keywords = 'digital products marketplace, sell online courses, ebook platform, zero platform fees, instant payouts, digital product creation, online business, passive income, creator economy, digital downloads, PayPal integration, no payment threshold, premium creator tools, digital marketing platform, online sales funnel',
  image = 'https://pokeshani.com/homepage-preview.png',
  url = 'https://pokeshani.com',
  type = 'website',
  siteName = 'PokeShani',
  locale = 'en_US',
  robots = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
}) => {
  // Global entity IDs. These match the @graph inlined in index.html so search
  // engines merge them into one entity instead of treating them as duplicates.
  const SITE = 'https://pokeshani.com';

  // NOTE: deliberately no SearchAction. PokeShani has no /search route, and
  // declaring one that 404s is a false signal to search engines.
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE}/#website`,
    name: 'PokeShani',
    url: SITE,
    publisher: { '@id': `${SITE}/#organization` },
    sameAs: [
      'https://twitter.com/pokeshani',
      'https://facebook.com/pokeshani',
      'https://instagram.com/pokeshani',
      'https://linkedin.com/company/pokeshani'
    ]
  };

  const organizationData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE}/#organization`,
    name: 'PokeShani',
    url: SITE,
    logo: {
      '@type': 'ImageObject',
      '@id': `${SITE}/#logo`,
      url: `${SITE}/favicon-512x512.png`,
      contentUrl: `${SITE}/favicon-512x512.png`,
      width: 512,
      height: 512
    },
    image: { '@id': `${SITE}/#logo` },
    description:
      'PokeShani is a digital products marketplace for independent creators. Sellers keep every cent of every sale, because PokeShani charges no platform fees.',
    foundingDate: '2024',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      availableLanguage: ['English'],
      areaServed: 'Worldwide'
    },
    sameAs: [
      'https://twitter.com/pokeshani',
      'https://facebook.com/pokeshani',
      'https://instagram.com/pokeshani',
      'https://linkedin.com/company/pokeshani'
    ]
  };

  const serviceData = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${SITE}/#service`,
    name: 'Digital Products Marketplace',
    provider: { '@id': `${SITE}/#organization` },
    description:
      'Platform for creating and selling digital products. Creators keep their own PayPal account and pay no platform fees.',
    serviceType: 'Digital Marketplace',
    areaServed: 'Worldwide',
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Digital Products',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Zero Platform Fees'
          }
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Direct PayPal Payouts'
          }
        }
      ]
    }
  };

  return (
    <Helmet>
      {/* Basic Meta Tags with Enhanced SEO */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="PokeShani Team" />
      <meta name="publisher" content="PokeShani" />
      <meta name="copyright" content="© 2024 PokeShani. All rights reserved." />
      <meta name="rating" content="General" />
      <meta name="distribution" content="Global" />
      <meta name="coverage" content="Worldwide" />
      <meta name="target" content="creators, entrepreneurs, digital marketers, online business owners" />

      {/* Enhanced Open Graph Meta Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="PokeShani - Zero Fees Digital Products Platform" />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={locale} />
      <meta property="og:updated_time" content={new Date().toISOString()} />
      <meta property="article:publisher" content="https://facebook.com/pokeshani" />
      <meta property="article:author" content="PokeShani Team" />
      <meta property="og:see_also" content="https://pokeshani.com/marketplace" />
      <meta property="og:see_also" content="https://pokeshani.com/signup" />

      {/* Enhanced Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@pokeshani" />
      <meta name="twitter:creator" content="@pokeshani" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content="PokeShani - Zero Fees Digital Products Platform" />
      <meta name="twitter:label1" content="Platform Fees" />
      <meta name="twitter:data1" content="0%" />
      <meta name="twitter:label2" content="Payout Time" />
      <meta name="twitter:data2" content="Instant" />

      {/* Advanced SEO and Performance Meta Tags */}
      <meta name="robots" content={robots} />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="3 days" />
      <meta name="author" content="PokeShani" />
      <meta name="theme-color" content="#1976d2" />
      <meta name="application-name" content="PokeShani" />
      <meta name="apple-mobile-web-app-title" content="PokeShani" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-touch-fullscreen" content="yes" />
      <meta name="HandheldFriendly" content="True" />
      <meta name="MobileOptimized" content="320" />
      <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="bingbot" content="index, follow" />
      <meta name="slurp" content="index, follow" />
      <meta name="facebookexternalhit" content="index, follow" />
      <meta name="twitterbot" content="index, follow" />

      {/* Enhanced Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(organizationData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(serviceData)}
      </script>

      {/* Performance and Preload Hints */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="preconnect" href="https://www.paypal.com" />
      <link rel="dns-prefetch" href="https://pokeshani.com" />
      <link rel="dns-prefetch" href="https://supabase.co" />

      {/* Canonical URL */}
      <link rel="canonical" href={url} />
    </Helmet>
  );
};

export default SEO;
